import React, { FC, ReactNode, isValidElement, ChangeEvent, useState, useEffect } from 'react';
import classNames from 'classnames/bind';

import Text, { TextProps } from '../Text';

import styles from './index.pcss';
import properties from '../../../modules/ajio-util/properties';
import { gtmEventPush, segmentEventPush } from '../../../modules/ga/GTMdatalayer';
import { isMobile } from '../../utils/deviceTypeUtils';

export type Id = string | number;
export type Size = 'sm' | 'md';
export type LabelType = string | ReactNode;

interface RatingLabelProps {
  htmlName?: string;
  totalRatingCount: string;
  totalStarCount: string;
  image: string;
  RatingCountBgColor: string;
  StarCountBgColor: string;
  rateDesign: string;
  rateCustomCss: object;
  isFleek?: boolean;
  ratingParityMsite?: boolean;
  luxe?: boolean;
}

const RatingLabel: FC<RatingLabelProps> = (props: RatingLabelProps) => {
  const {
    totalRatingCount,
    totalStarCount,
    image,
    RatingCountBgColor,
    StarCountBgColor,
    rateDesign,
    rateCustomCss,
    productName,
    productId,
    brickCode,
    sourcePage,
    isFleek = false,
    ratingParityMsite = false,
    luxe = false
  } = props;
  const imageBaseUrl: string = properties.getProperty('imageBaseUrl');
  const starUrl = isFleek
    ? `${imageBaseUrl}/static/img/black-star-rating.svg`
    : image
    ? `${imageBaseUrl}/static/img/${image}`
    : `${imageBaseUrl}/static/img/green_star_pdp.svg`;
  const starUrlAltText = isFleek
    ? 'Star selected icon'
    : image
    ? ''
    : 'Star icon';
  const cx = classNames.bind(styles);
  const containerCls = cx('container', {
    [`container-${rateDesign}`]: !!rateDesign
  });
  const ratingCountCls = cx('rating-count', isFleek && 'fleek-count');
  const starCountCls = cx('star-count', isFleek && 'fleek-count');
  const isMobileDevice = isMobile();

  useEffect(() => {
    if (sourcePage === 'PDP') {
      gtmEventPush({
        event: 'reviews_rating_interactions',
        event_category: 'reviews & ratings interactions',
        event_action: 'Rating shown',
        event_label: `${totalRatingCount} | ${totalStarCount}`,
        page_type: 'product page',
        product_name: productName,
        product_id: productId,
        product_brick: brickCode,
        ratings: totalRatingCount,
        no_of_users_shown: totalStarCount
      });      
    }
  }, []);

  const getStarIcon = (value: number) => {
    if (value >= 1) return `${imageBaseUrl}/static/img/black-star-one.svg`;
    if (value >= 0.6) return `${imageBaseUrl}/static/img/black-star-point-six.svg`;
    if (value >= 0.5) return `${imageBaseUrl}/static/img/black-star-point-five.svg`;
    if (value > 0) return `${imageBaseUrl}/static/img/black-star-point-four.svg`;
    return `${imageBaseUrl}/static/img/black-star-zero.svg`;
  };

  const getStar = () => {
    const stars = Array.from({ length: 5 }, (_, i) => {
      const value = Math.min(Math.max(totalRatingCount - i, 0), 1);
      const icon = getStarIcon(value);

      return (
        <img
          key={i}
          src={icon}
          alt={`star-${i + 1}`}
        />
      );
    });
    return stars;
  }

  if(!luxe && ratingParityMsite && isMobileDevice){
    return(
      <div
        className={`${containerCls} ${cx('black-star-rating-container')}`}
        // style={{ backgroundColor: RatingCountBgColor }}
        aria-label={`${totalRatingCount} stars rating out of 5 stars`}
      >
        <span className={starCountCls} aria-hidden="true">
          {getStar()}
        </span>
      </div>
    )
  }
  

  return (
    <>
      {rateDesign.toUpperCase() === 'WIDGET' ? (
        <div
          className={`${containerCls} ${cx('rating-label-widget')}`}
          style={{ backgroundColor: RatingCountBgColor }}
          aria-label={`${totalRatingCount} stars rating out of 5 stars`}
        >
          <span className={starCountCls} aria-hidden="true">
            {totalRatingCount} <img className="imageIcon" src={starUrl} alt={starUrlAltText} />{' '}
          </span>
          <span className={ratingCountCls} aria-hidden="true"> | {totalStarCount}</span>
        </div>
      ) : (
        <>
          <div
            className={`${containerCls} ${cx('rating-label-rate-count')}`}
            style={{ backgroundColor: RatingCountBgColor }}
            aria-label={`${totalRatingCount} star by ${totalStarCount} Ratings`}
            // role='region'
          >
            <span className={starCountCls} aria-hidden='true'>
              {totalRatingCount} <img className="imageIcon" src={starUrl} alt={starUrlAltText}/>{' '}
            </span>
          </div>
          <div
            className={`${containerCls} ${cx('rating-label-star-count')}`}
            style={{ backgroundColor: StarCountBgColor }}
            aria-hidden='true'
            tabIndex={-1}
          >
            <span className={ratingCountCls} style={rateCustomCss}>
              {totalStarCount} Ratings
            </span>
          </div>
        </>
      )}
    </>
  );
};

RatingLabel.defaultProps = {
  totalRatingCount: '0',
  totalStarCount: '0',
  image: 'green_star_pdp.svg',
  RatingCountBgColor: '#008526',
  StarCountBgColor: '#ffffff',
  rateDesign: '',
  rateCustomCss: {}
};

export default RatingLabel;
